import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  inject,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ButtonModule } from 'primeng/button';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { ToastModule } from 'primeng/toast';
import { MessageService } from 'primeng/api';
import { of } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { BatchManagementService } from '@app/api/stock-service/api/batchManagement.service';
import { BlobConverterUtil } from '@app/shared/utils/blob-converter.util';
import { AppButtonComponent } from '@shared/components/app-button/app-button.component';

@Component({
  selector: 'app-batch-detail',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    ButtonModule,
    TableModule,
    TagModule,
    ToastModule,
    AppButtonComponent,
  ],
  providers: [MessageService],
  templateUrl: './batch-detail.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BatchDetailComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly batches = inject(BatchManagementService);
  private readonly translate = inject(TranslateService);
  private readonly messageService = inject(MessageService);

  loading = signal(true);
  batch = signal<any | null>(null);

  ngOnInit(): void {
    const id = Number(this.route.snapshot.paramMap.get('id'));
    if (!id || Number.isNaN(id)) {
      this.loading.set(false);
      return;
    }
    this.batches
      .detail9(id)
      .pipe(
        switchMap((response) =>
          BlobConverterUtil.isBlob(response)
            ? BlobConverterUtil.convertToJson(response)
            : of(response),
        ),
        catchError(() => {
          this.messageService.add({
            severity: 'error',
            summary: this.translate.instant('common.messages.error'),
            detail: this.translate.instant('batch_management.messages.failed_to_load'),
          });
          return of(null);
        }),
      )
      .subscribe((detail) => {
        this.batch.set(detail);
        this.loading.set(false);
      });
  }

  back(): void {
    this.router.navigate(['/stock/batch']);
  }

  formatSourceType(sourceType?: string): string {
    if (!sourceType) {
      return '-';
    }
    const key = 'lot_trace.source_types.' + sourceType;
    const translated = this.translate.instant(key);
    return translated === key ? sourceType : translated;
  }

  formatDateTime(value?: string | Date): string {
    if (!value) {
      return '-';
    }
    const d = typeof value === 'string' ? new Date(value) : value;
    if (Number.isNaN(d.getTime())) {
      return '-';
    }
    return d.toLocaleString('vi-VN');
  }

  formatDate(value?: string | Date): string {
    if (!value) {
      return '-';
    }
    const d = typeof value === 'string' ? new Date(value) : value;
    if (Number.isNaN(d.getTime())) {
      return '-';
    }
    return d.toLocaleDateString('vi-VN');
  }
}
